import { useEffect, useState } from 'react';
import axiosInstance from '../../../../../api/axiosInstance';
import styles from '../HomepageSettings.module.css';

// Countries linked to a holiday type in GeoData. This is exactly what the public
// /holidays/:slug page already lists, so the editor can see what the site shows
// today before deciding which destinations to surface on the card itself.
//
// Cached per holiday type for the lifetime of the page — several cards can ask
// for the same id, and the list only changes in GeoData, not here.
const cache = new Map();

const loadCountries = (id) => {
  if (!cache.has(id)) {
    cache.set(
      id,
      axiosInstance
        .get(`/website/holiday-types/${encodeURIComponent(id)}/countries`)
        .then((res) => res?.data?.data?.countries ?? [])
        .catch((err) => {
          cache.delete(id); // let a later expand retry
          throw err;
        })
    );
  }
  return cache.get(id);
};

const MAX_SHOWN = 12;

export default function HolidayTypeLiveCountries({ holidayTypeId }) {
  const [state, setState] = useState({ status: 'loading', countries: [] });

  useEffect(() => {
    if (holidayTypeId == null) {
      setState({ status: 'none', countries: [] });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', countries: [] });
    loadCountries(holidayTypeId)
      .then((countries) => {
        if (!cancelled) setState({ status: 'ok', countries });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', countries: [] });
      });
    return () => { cancelled = true; };
  }, [holidayTypeId]);

  if (state.status === 'none') {
    return <p className={styles.liveEmpty}>Select a holiday type to see what the website shows.</p>;
  }
  if (state.status === 'loading') {
    return <p className={styles.liveEmpty}>Loading what the website shows…</p>;
  }
  if (state.status === 'error') {
    return <p className={styles.liveEmpty}>Could not load the current website list.</p>;
  }
  if (!state.countries.length) {
    return (
      <p className={styles.liveEmpty}>
        No countries linked yet — the holiday type page is empty. Link countries in
        {' '}<strong>GeoData → Countries</strong>.
      </p>
    );
  }

  const shown = state.countries.slice(0, MAX_SHOWN);
  const rest = state.countries.length - shown.length;

  return (
    <div className={styles.liveChips}>
      {shown.map((c) => (
        <span key={c.id ?? c.code} className={styles.liveChip} title={`${c.name} · ${c.code}`}>
          {c.flagUrl ? (
            <img className={styles.liveFlag} src={c.flagUrl} alt="" loading="lazy" />
          ) : (
            <span className={styles.liveFlagEmoji}>{c.flag || '🏳️'}</span>
          )}
          {c.name}
        </span>
      ))}
      {rest > 0 && <span className={styles.liveMore}>+{rest} more</span>}
    </div>
  );
}
